import React, { Suspense, useEffect } from 'react';
import { initAeroSyncWidget } from 'aerosync-web-sdk';
import { getLogger } from 'src/utils/datadog-logger';
import { useAerosync } from 'src/payments/hooks/use-aerosync-widget';
import { FullPageLoader } from 'shared/components';
import { Aggregator, EnrollmentSteps, IS_E2E } from 'src/payments/constants';
import { BankConnectionContexts } from 'src/payments/hooks';
import { useTrackEnrollmentStepViewed } from 'src/payments/hooks/use-dutchie-pay-analytics/use-track-enrollment-step-viewed';

const logger = getLogger('aerosync-widget');

const MockAggregatorWidget = React.lazy(() => import('src/payments/components/mock-aggregator-widget'));

export function AeroSyncWidget(): JSX.Element | null {
  const { aeroSyncWidgetOptions, isReady, AEROSYNC_WIDGET_ID } = useAerosync();
  const aeroSyncWidgetRef = useTrackEnrollmentStepViewed({
    step: EnrollmentSteps.startEnrollment,
    context: BankConnectionContexts.BANK_CONNECTION_AEROSYNC,
  });

  useEffect(() => {
    // In E2E mode the real widget is never initialized — the mock widget rendered
    // below handles enrollment success/exit directly, bypassing the third-party SDK.
    if (IS_E2E) {
      return undefined;
    }

    if (!isReady) {
      return undefined;
    }

    const widgetRef = initAeroSyncWidget(aeroSyncWidgetOptions);
    widgetRef.launch();

    const cleanup = (): void => {
      try {
        widgetRef.destroy();
      } catch (error) {
        logger.error('Aerosync widget error', { error });
      }
    };

    return cleanup;
  }, [isReady, aeroSyncWidgetOptions]);

  // In E2E mode show the mock widget immediately, synchronously, on the very first render
  // — same pattern as the sibling AggregatorWidget (Plaid) component. This used to be gated
  // behind a post-mount effect + state flip (`useState` + `setDisplayMockAeroSyncWidget(true)`
  // inside a `useEffect`), which let the real-widget branch below (the bare
  // `<div id={AEROSYNC_WIDGET_ID}/>` + FullPageLoader, before `isReady` from the real Aerosync
  // SDK ever resolves in E2E mode) render for at least one frame before flipping to the mock.
  // That extra render pass is a real race: anything that acts on mount (Playwright waiting for
  // the mock's testids, `useTrackEnrollmentStepViewed`'s impression tracking) could observe the
  // real-widget frame first, and CI's timing made that race land often enough to quarantine the
  // AeroSync enrollment E2E tests (ECOMM-11757, ECOMM-11762, ECOMM-11763) while the structurally
  // identical Plaid tests, whose AggregatorWidget never had this indirection, stayed stable.
  if (IS_E2E) {
    return (
      <Suspense fallback={null}>
        <MockAggregatorWidget
          aggregator={Aggregator.AEROSYNC}
          onSuccess={aeroSyncWidgetOptions.onSuccess}
          onExit={aeroSyncWidgetOptions.onClose}
        />
      </Suspense>
    );
  }

  return (
    <>
      <div id={AEROSYNC_WIDGET_ID} ref={aeroSyncWidgetRef} />
      {!isReady && <FullPageLoader />}
    </>
  );
}
